import React, { useState, useEffect } from 'react';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import { makeStyles } from '@material-ui/core/styles';

import { Link } from 'react-router-dom';

//iconos
import { Create, Add, Weekend, Visibility } from '@material-ui/icons';

//tabla y card components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import ReactTable from 'components/ReactTable/ReactTableV2.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

//servicios
import { wsPost, wsFindBy } from 'services/WService.js';
import FilterBusinessPartner from 'components/Tvendo/Common/FilterBusinessPartner';
import FilterFurnitureCategory from './FilterFurnitureCategory';
import FilterFurnitureState from './FilterFurnitureState';

//modal
import Modal from './Modal';
import ButtonBaja from './ButtonBaja';
import { nombreEstado } from './estados';
import { useModal } from 'hooks/useModal';
import { Toasty, useToasty } from 'hooks/Toasty';
import ButtonDelete from 'hooks/ButtonDelete';
import { WithApp } from 'contexts/appContext';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  }
};
const useStyles = makeStyles(styles);

//columnas para llenar la tabla
const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 180 },
  { Header: 'Cód. Cliente', accessor: 'CardCode', width: 120 },
  { Header: 'Nombre Cliente', accessor: 'CardName' },
  { Header: 'Dirección', accessor: 'Address', width: 180 },
  { Header: 'Mueble', accessor: 'furniture', width: 200 },
  { Header: 'Foto', accessor: 'photo', excelEnabled: false, width: 80 },
  { Header: 'Estado', accessor: 'estado', width: 110 },
  { Header: 'Fecha de alta', accessor: 'registered', width: 130 },
  { Header: 'Fecha de baja', accessor: 'unregistered', width: 130 },
  { Header: 'Última compra Arcor', accessor: 'lastPurchase', width: 160 },
  { Header: 'Venta Arcor 2 meses', accessor: 'sales', width: 160 },
  { Header: 'Categoría', accessor: 'category', width: 130 },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

const moneda = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Control de muebles: qué mueble tiene colocado cada cliente.
 *
 * La venta y la categoría las calcula el proceso `muebles:recalcular`, no esta
 * pantalla: con miles de registros calcularlas al vuelo la dejaría inservible.
 * Por eso hay un botón para forzarlo cuando hace falta verlo al día.
 */
export default WithApp(function CustomerFurniture(props) {
  const classes = useStyles();

  const [model, setOpen] = useModal();

  const [notify, showNotify] = useToasty();
  const [controles, setControles] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [valueSearch, setValueSearch] = useState({});

  async function loadControles(search = valueSearch) {
    //se limpian las claves vacías: si no, el backend filtra por '' y no trae nada
    let filtros = { ...search };
    for (var clave in filtros) {
      if (filtros[clave] === null || filtros[clave] === '') delete filtros[clave];
    }

    setDataLoading(true);
    let response = await wsFindBy('base/customer-furniture', filtros);
    setDataLoading(false);
    if (response.success) setControles(response.data);
  }

  async function recalcular() {
    setDataLoading(true);
    let response = await wsPost('base/customer-furniture/recalculate', {});
    setDataLoading(false);

    if (response.success) {
      showNotify(response.message);
      loadControles();
    } else {
      showNotify(response.message, 'error');
    }
  }

  useEffect(() => {
    loadControles();
  }, []);

  return (
    <GridContainer>
      <Toasty action={notify} />
      {model.open ? (
        <Modal model={model} setOpen={setOpen} showNotify={showNotify} reload={loadControles} />
      ) : undefined}

      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <AllowedGuard
              permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}
              component={
                <CardIcon color="primary">
                  <Weekend style={{ color: 'white' }} />
                </CardIcon>
              }
            >
              <CardIcon
                onClick={() => setOpen({})}
                pointer="true"
                color={props.themeContext.buttonColorSuccess}
              >
                <Add />
              </CardIcon>
            </AllowedGuard>
            <h4 className={classes.cardIconTitle}>
              Control de muebles {dataLoading && <LoadingSpinner />}
              <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}>
                <Button
                  size="sm"
                  color="warning"
                  style={{ marginLeft: '10px' }}
                  onClick={recalcular}
                  disabled={dataLoading}
                >
                  Recalcular ventas
                </Button>
              </AllowedGuard>
            </h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <FilterBusinessPartner
                value={valueSearch}
                getValue={setValueSearch}
                getData={() => loadControles(valueSearch)}
              />
              <FilterFurnitureCategory
                value={valueSearch}
                getValue={setValueSearch}
                getData={(nuevo) => loadControles(nuevo)}
              />
              <FilterFurnitureState
                value={valueSearch}
                getValue={setValueSearch}
                getData={(nuevo) => loadControles(nuevo)}
              />
            </GridContainer>

            <ReactTable
              excelName="Control de muebles"
              defaultWidth={1700}
              columns={columns}
              data={controles.map((item, i) => {
                return {
                  num: (
                    <div className="actions-right">
                      {i + 1}
                      {/* al detalle: ventas del cliente e histórico de categorías */}
                      <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}>
                        <Link to={`${item.id}`}>
                          <Visibility />
                        </Link>
                      </AllowedGuard>
                      <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}>
                        <Button
                          justIcon
                          round
                          simple
                          color={props.themeContext.buttonColor}
                          onClick={() => setOpen(item)}
                        >
                          <Create />
                        </Button>
                      </AllowedGuard>
                      {/* dar de baja: pone la fecha de hoy y libera el mueble */}
                      {item.state !== 'B' && (
                        <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}>
                          <ButtonBaja
                            id={item.id}
                            reload={loadControles}
                            showNotify={showNotify}
                          />
                        </AllowedGuard>
                      )}
                      <AllowedGuard permission={ConstantPermissions.BASE_BUSINESS_PARTNERS_CUSTOMER_INDEX}>
                        <ButtonDelete
                          entity="base/customer-furniture"
                          id={item.id}
                          reload={loadControles}
                        />
                      </AllowedGuard>
                    </div>
                  ),
                  CardCode: item.customer?.CardCode || '',
                  CardName: item.customer?.CardName || '',
                  Address: item.address?.Address2 || '',
                  furniture: item.furniture
                    ? `${item.furniture.code ? item.furniture.code + ' - ' : ''}${item.furniture.description}`
                    : '',
                  photo: item.furniture?.file_url ? (
                    <a href={item.furniture.file_url} target="_blank" rel="noreferrer">
                      <img
                        src={item.furniture.file_url}
                        alt={item.furniture.description || 'Mueble'}
                        style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4 }}
                      />
                    </a>
                  ) : (
                    ''
                  ),
                  estado: nombreEstado(item.state),
                  //la fecha de alta es la de creación: registered_at queda sin usarse
                  registered: String(item.created_at || '').slice(0, 10),
                  unregistered: item.unregistered_at || '',
                  //la calcula el backend en una sola consulta para toda la grilla
                  lastPurchase: item.last_purchase_at
                    ? String(item.last_purchase_at).slice(0, 10)
                    : 'Sin compras',
                  //null mientras el recálculo no haya corrido nunca
                  sales: moneda(item.sales_amount),
                  category: item.sales_category?.description || '',
                  actions: '',
                };
              })}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});
